/**
 * 🚨 CRITICAL COMPONENT - STICKER MODAL
 *
 * Main modal for sticker details and purchasing. Must load images instantly.
 *
 * ⚠️ BEFORE MODIFYING:
 * 1. Read /docs/SYSTEMS/MODAL_IMAGE_PRIORITY_SYSTEM.md
 * 2. Read /docs/ESSENTIAL/CRITICAL_SYSTEMS.md
 * 3. Check modal height coordination (610px)
 *
 * 🔒 CRITICAL RULES:
 * - NEVER change isPriorityLoad default to false (line 15)
 * - ALWAYS keep modal max-height at 610px (StickerModal.css line 22)
 * - NEVER remove blur overlay height calculation (StickerModal.css line 7)
 * - ALWAYS coordinate with grid scroll offset (NewHomePage.js line 485)
 *
 * 📍 CRITICAL SECTIONS:
 * - Line 15: isPriorityLoad = true (MUST stay true)
 * - Etsy deeplink handling with variation URLs
 *
 * 🎯 MODAL POSITIONING:
 * - Modal height: 610px (max-height in CSS)
 * - Blur overlay: calc(100vh - 610px)
 * - Grid offset: 660px (610px + 50px spacing)
 *
 * Last reviewed: 2025-01-15
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import CoreStickerCard from './CoreStickerCard';
import MaterialIcon from './MaterialIcon';
import { trackEtsyClick } from '../services/analyticsService';
import { buildEtsyVariationUrl } from '../utils/etsyVariationUrls';
import { useCart } from '../contexts/CartContext';
import navigationService from '../services/navigationService';
import featureFlags from '../config/featureFlags';
import logger from '../utils/logger.js';
import './StickerModal.css';

const StickerModal = ({
  isOpen,
  selectedListing,
  onClose,
  onAddToScene = () => {},
  isPriorityLoad = true, // 🎯 ALWAYS use priority loading for modals
  onImageLoad = () => {}, // Callback when modal image loads to resume grid loading
}) => {
  const [modalPrice, setModalPrice] = useState(null);
  const [shareToast, setShareToast] = useState(false);
  const [currentListing, setCurrentListing] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [addToCartSuccess, setAddToCartSuccess] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState('');

  const { addItem } = useCart();

  const handlePriceUpdate = useCallback((price) => {
    setModalPrice(price);
  }, []);

  // Track size/color selection from CoreStickerCard
  const handleSizeColorUpdate = useCallback((size, color) => {
    setSelectedSize(size);
    setSelectedColor(color);
  }, []);

  // Track current image from CoreStickerCard
  const handleImageUpdate = useCallback((imageUrl) => {
    setCurrentImageUrl(imageUrl);
  }, []);

  // Add to cart handler
  const handleAddToCart = useCallback(() => {
    if (!currentListing || !modalPrice || !selectedSize) {
      console.error('❌ Missing required data for add to cart');
      return;
    }

    // Use the current image URL from CoreStickerCard
    const imageUrl = currentImageUrl || '';

    // Get variation IDs for Etsy deeplinks
    const sizeVariationId = currentListing.sizeVariations?.[selectedSize];
    const colorVariationId = selectedColor ? currentListing.colorVariations?.[selectedColor] : null;

    // Build cart item
    const cartItem = {
      listingId: currentListing.etsyListingId || currentListing.listingId,
      title: currentListing.title,
      price: modalPrice,
      quantity: 1,
      size: selectedSize,
      color: selectedColor || null,
      imageUrl: imageUrl,
      etsyUrl: currentListing.etsyUrl || currentListing.etsy_url,
      sizeVariationId: sizeVariationId,
      colorVariationId: colorVariationId,
      isSubscriberOnly:
        currentListing.subscriber_only === 1 || currentListing.subscriber_only === true,
      isEarlyAccess: currentListing.early_access_until
        ? new Date(currentListing.early_access_until) > new Date()
        : false,
    };

    // Add to cart
    const result = addItem(cartItem);

    if (result.success) {
      logger.log('✅ Added to cart:', cartItem.title, `(${cartItem.size})`, 'Image:', imageUrl);

      // Show success toast
      setAddToCartSuccess(true);
      setTimeout(() => setAddToCartSuccess(false), 2000);
    }
  }, [currentListing, modalPrice, selectedSize, selectedColor, addItem]);

  // Share function to copy Etsy URL to clipboard
  const handleShare = async () => {
    const etsyUrl = currentListing.etsyUrl || currentListing.etsy_url;
    if (!etsyUrl) {
      console.error('❌ No Etsy URL found for sharing');
      return;
    }

    try {
      await navigator.clipboard.writeText(etsyUrl);
      setShareToast(true);

      // Track the share action for analytics
      const titleForAnalytics =
        currentListing.title && currentListing.title.length > 20
          ? currentListing.title.substring(0, 20)
          : currentListing.title;

      trackEtsyClick(
        currentListing.etsyListingId || currentListing.listingId,
        titleForAnalytics,
        modalPrice ? `$${modalPrice.toFixed(2)}` : 'unknown',
        currentListing.clickedSize || 'unknown',
        'share'
      );

      logger.log('📤 Etsy URL copied to clipboard for sharing');
    } catch (error) {
      console.error('❌ Failed to copy to clipboard:', error);
      // Fallback: try to open share dialog or show manual copy instruction
      const shareText = `Check out this sticker: ${etsyUrl}`;
      if (navigator.share) {
        try {
          await navigator.share({ title: currentListing.title, url: etsyUrl });
        } catch (shareError) {
          console.error('❌ Share dialog failed:', shareError);
        }
      }
    }
  };

  // Auto-hide share toast after 3 seconds
  useEffect(() => {
    if (shareToast) {
      const timer = setTimeout(() => {
        setShareToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [shareToast]);

  // Update current listing when selectedListing changes
  useEffect(() => {
    // IMPORTANT: Do NOT call navigationService.openModal here to avoid feedback loops.
    setCurrentListing(selectedListing);
    setModalPrice(null); // Reset price when listing changes
    setShareToast(false); // Hide any existing toast
    setNavigationHistory([]); // Reset navigation history when opening new modal
  }, [selectedListing, isOpen]);

  // Handle clicking on a recommendation (switch to that listing)
  const handleRecommendationClick = (recommendedListing) => {
    // Add current listing to history before switching
    setNavigationHistory((prev) => [...prev, currentListing]);
    setCurrentListing(recommendedListing);
    setModalPrice(null); // Reset price for new listing
    setShareToast(false); // Hide any existing toast
    logger.log(`🔄 Modal switched to recommended listing: ${recommendedListing.title}`);
  };

  // Handle back button click
  const handleBackClick = () => {
    if (navigationHistory.length > 0) {
      const previousListing = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory((prev) => prev.slice(0, -1)); // Remove last item from history
      setCurrentListing(previousListing);
      setModalPrice(null); // Reset price for previous listing
      setShareToast(false); // Hide any existing toast
      logger.log(`⬅️ Modal navigated back to: ${previousListing.title}`);
    }
  };

  // Allow background scrolling when modal is open (we want the feed to be scrollable)
  useEffect(() => {
    // Don't disable body scroll - we want the feed below to be scrollable
    return () => {
      // Cleanup only
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleModalClose = () => {
    // Only signal close to parent; navigation is centrally handled
    setModalPrice(null);
    onClose();
  };

  // Use the same shortening logic as URL slug, humanized for display
  const shortModalTitle = useMemo(() => {
    // Prefer the active modal slug (deep links) → then URL path → then generated from title
    const modalState = navigationService.getModalState?.() || null;
    const slugFromState = modalState?.slug;
    const slugFromPath =
      typeof window !== 'undefined'
        ? window.location.pathname.replace(/^\//, '').replace(/\/$/, '')
        : '';
    let slug = slugFromState || slugFromPath;
    if (!slug) {
      const title = currentListing?.title || '';
      slug = navigationService.generateSlug(title);
    } else {
      // Normalize: trim trailing stopwords in path-derived slug for consistency
      const STOPWORDS = new Set([
        'and',
        'or',
        'with',
        'the',
        'a',
        'an',
        'of',
        'for',
        'to',
        'in',
        'on',
        'at',
      ]);
      const words = slug.split('-').filter(Boolean);
      while (words.length > 0 && STOPWORDS.has(words[words.length - 1])) {
        words.pop();
      }
      slug = words.join('-');
    }
    const tokens = (slug || '').split('-').filter(Boolean);
    // Ensure vinyl-decal suffix is present in display with separator bar
    const hasVinyl = tokens.slice(-2).join('-') === 'vinyl-decal';
    const baseTokens = hasVinyl ? tokens.slice(0, -2) : tokens;
    const humanBase = baseTokens.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const human = `${humanBase} | Vinyl Decal`;
    return human || 'Sticker Details';
  }, [currentListing?.title]);

  if (!isOpen || !currentListing) {
    return null;
  }

  return (
    <div className="sticker-modal-overlay" onClick={handleModalClose}>
      <div className="sticker-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-left">
            <h2>{shortModalTitle || 'Sticker Details'}</h2>
            {/* TRUEscale Badge */}
            {(currentListing.isTrueScale === 1 || currentListing.isTrueScale === true) && (
              <div className="modal-truescale-badge">
                <img
                  src="/assets/images/trueScale.png"
                  alt="TRUEscale"
                  className="modal-truescale-logo"
                />
              </div>
            )}
          </div>
          <div className="modal-header-actions">
            <div className="header-buy-wrapper">
              {/* Add to Cart Button - Buy Direct - 🚧 Feature Flag Controlled */}
              {featureFlags.enableShoppingCart && (
                <button
                  className="modal-add-to-cart-button"
                  onClick={handleAddToCart}
                  disabled={!modalPrice || !selectedSize}
                  title="Add to cart for direct purchase on ClothedEye.com"
                >
                  <MaterialIcon icon="add_shopping_cart" size={16} />
                  ADD TO CART {modalPrice ? `$${modalPrice.toFixed(2)}` : ''}
                </button>
              )}

              {/* Buy It Now Button - Etsy */}
              <button
                className="modal-buy-now-button"
                onClick={() => {
                  const baseUrl = currentListing.etsyUrl || currentListing.etsy_url;
                  if (baseUrl) {
                    // Build variation URL if we have the data
                    const variationUrl = buildEtsyVariationUrl(
                      baseUrl,
                      currentListing.sizeVariations,
                      currentListing.colorVariations,
                      selectedSize,
                      selectedColor
                    );

                    const finalUrl = variationUrl || baseUrl;
                    window.open(finalUrl, '_blank', 'noopener,noreferrer');

                    logger.log('🛒 Opening Etsy URL:', finalUrl);
                    if (variationUrl && variationUrl !== baseUrl) {
                      logger.log('✅ Using variation URL with:', {
                        size: selectedSize,
                        color: selectedColor,
                      });
                    }

                    const titleForAnalytics =
                      currentListing.title && currentListing.title.length > 20
                        ? currentListing.title.substring(0, 20)
                        : currentListing.title;
                    const priceForAnalytics = modalPrice ? `$${modalPrice.toFixed(2)}` : null;
                    const sizeForAnalytics = selectedSize || currentListing.clickedSize || null;
                    const colorForAnalytics = selectedColor || null;

                    if (titleForAnalytics && priceForAnalytics && sizeForAnalytics) {
                      trackEtsyClick(
                        currentListing.etsyListingId || currentListing.listingId,
                        titleForAnalytics,
                        priceForAnalytics,
                        `${sizeForAnalytics} - ${colorForAnalytics}`
                      );
                    }
                  } else {
                    console.error('❌ No Etsy URL found in currentListing');
                  }
                }}
              >
                <MaterialIcon icon="shopping_cart" size={16} />
                Buy on Etsy {modalPrice ? `$${modalPrice.toFixed(2)}` : ''}
              </button>
            </div>
            {navigationHistory.length > 0 && (
              <button
                className="modal-back-btn"
                onClick={handleBackClick}
                title="Go back to previous sticker"
              >
                <MaterialIcon icon="arrow_back" size={20} />
              </button>
            )}
            <button className="modal-share-btn" onClick={handleShare} title="Share Etsy link">
              <MaterialIcon icon="share" size={20} />
            </button>
            <button className="modal-close-btn" onClick={handleModalClose}>
              <MaterialIcon icon="close" size={24} />
            </button>
          </div>
        </div>
        <div className="modal-body">
          <CoreStickerCard
            key={currentListing.etsyListingId || currentListing.listingId}
            listingId={currentListing.etsyListingId}
            globalSize={currentListing.clickedSize || 'MICRO'}
            globalColor={currentListing.clickedColor || 'Black (Matte)'}
            etsyUrl={currentListing.etsy_url}
            onAdd={onAddToScene}
            onPriceUpdate={handlePriceUpdate}
            onSizeColorChange={handleSizeColorUpdate}
            onImageChange={handleImageUpdate}
            modalMode={true}
            isPriorityLoad={isPriorityLoad}
            onImageLoad={onImageLoad}
          />
        </div>
        {/* Similar items divider indicator */}
        <div className="modal-similar-indicator" aria-hidden="true">
          <span className="similar-text">Check Out Similar Designs Below</span>
          <MaterialIcon icon="move_down" size={18} />
        </div>

        {/* Share Success Toast */}
        {shareToast && (
          <div className="share-toast">
            <MaterialIcon icon="check_circle" size={16} />
            Etsy link copied! Share it to drive sales.
          </div>
        )}

        {/* Add to Cart Success Toast - 🚧 Feature Flag Controlled */}
        {featureFlags.enableShoppingCart && addToCartSuccess && (
          <div className="cart-success-toast">
            <MaterialIcon icon="check_circle" size={16} />
            Added to cart! Click the green cart icon to checkout.
          </div>
        )}
      </div>
    </div>
  );
};

StickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  selectedListing: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onAddToScene: PropTypes.func,
  isPriorityLoad: PropTypes.bool,
  onImageLoad: PropTypes.func,
};

export default StickerModal;
